import React from 'react';
import { 
  Security as RolesIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  MoreVert as MoreIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { Chit, ChitContent, ChitMain, ChitText, ChitActions, ChitActionButton, ChitAvatar } from 'src/components/ui/chit';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import type { DemoCustomRole } from '@/types/demo.types';

interface RoleChitProps {
  role: DemoCustomRole;
  onEdit: (role: DemoCustomRole) => void;
  onDelete: (roleId: string, roleName: string) => void;
  onViewMembers: (roleName: string) => void;
  getPermissionLabel: (permissionKey: string) => string;
  formatDate: (dateString: string) => string;
}

export const RoleChit: React.FC<RoleChitProps> = ({
  role,
  onEdit,
  onDelete,
  onViewMembers,
  getPermissionLabel,
  formatDate
}) => {
  const handleViewMembers = () => {
    onViewMembers(role.name);
  };

  const handleDelete = () => {
    onDelete(role.customRoleId, role.name);
  };

  return (
    <Chit>
      <ChitContent>
        <ChitMain>
          <ChitAvatar 
            icon={<RolesIcon />} 
            size="lg"
            className="bg-purple-500/20 text-purple-400"
          />
          <ChitText
            title={role.name}
            subtitle={
              <span className="flex items-center gap-2">
                <PeopleIcon className="w-4 h-4" />
                {role.memberCount} members
                <span className="text-white/20">•</span>
                <span>{role.permissions.length} permissions</span>
                <span className="text-white/20">•</span>
                <span>Created {formatDate(role.createdDate)}</span>
              </span>
            }
            description={
              <div>
                {role.description && (
                  <p className="text-white/30 text-xs mb-2">{role.description}</p>
                )}
                <div className="flex flex-wrap gap-1">
                  {role.permissions.slice(0, 3).map((permission) => (
                    <span
                      key={permission}
                      className="px-2 py-1 rounded-full bg-purple-500/10 text-purple-300 text-xs"
                    >
                      {getPermissionLabel(permission)}
                    </span>
                  ))}
                  {role.permissions.length > 3 && (
                    <span className="px-2 py-1 rounded-full bg-white/5 text-white/40 text-xs">
                      +{role.permissions.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            }
            titleClassName="text-lg"
            subtitleClassName="text-white/40 text-sm"
            descriptionClassName="text-white/30 text-xs"
          />
        </ChitMain>
        
        <ChitActions>
          <ChitActionButton
            onClick={() => onEdit(role)}
            icon={<EditIcon />}
            size="sm"
          >
            Edit
          </ChitActionButton>
          
          {/* 3-dot menu with Popover */}
          <Popover>
            <PopoverTrigger render={() => (
              <button className="text-white/40 bg-transparent border-none cursor-pointer p-2 rounded-md flex items-center hover:bg-white/10 transition-colors">
                  <MoreIcon className="w-5 h-5" />
                </button>
              )}
            />
            <PopoverContent 
              className="w-48 p-0 bg-neutral-900 border-white/20"
              align="end"
              side="bottom"
            >
              <div className="overflow-hidden">
                <button
                  onClick={handleViewMembers}
                  className="w-full px-3 py-2 text-left text-white/80 hover:bg-white/10 flex items-center gap-2 text-sm first:rounded-t-lg"
                >
                  <ViewIcon className="w-4 h-4" />
                  View Members
                </button>
                <button
                  onClick={handleDelete}
                  className="w-full px-3 py-2 text-left text-red-400 hover:bg-red-500/10 flex items-center gap-2 text-sm border-t border-white/10 last:rounded-b-lg"
                >
                  <DeleteIcon className="w-4 h-4" />
                  Delete Role
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </ChitActions>
      </ChitContent>
    </Chit>
  );
};